<script setup lang="ts">
import { toast } from '@/composables/baseToast'
import { supabase } from '@/composables/supabaseClient'
import numberToCurrency from '@/helpers/numberToCurrency'
import uuidv7 from '@/helpers/uuidv7'
import { useDatabaseStore } from '@/stores/databaseStore'
import type { Tables } from '@/types/database.types'
import { computed, ref } from 'vue'
import type { TableColumn } from './BaseTable.vue'
import BaseTable from './BaseTable.vue'

const props = defineProps<{ assemblyId: string }>()

const databaseStore = useDatabaseStore()

const rows = computed(() => {
  const defaultCostSource = databaseStore.costSources?.find(
    (costSource) => costSource.name == 'Default',
  )
  return (
    databaseStore.items?.map((item) => {
      const defaultCosts = databaseStore.itemCosts?.find(
        (cost) => cost.cost_source_id == defaultCostSource?.id && cost.item_id == item.id,
      )
      const unitCost = defaultCosts
        ? (defaultCosts.material_cost ?? 0) +
          (defaultCosts.labor_cost ?? 0) +
          (defaultCosts.equipment_cost ?? 0) +
          (defaultCosts.subcontractor_cost ?? 0) +
          (defaultCosts.other_cost ?? 0)
        : 0
      const division = databaseStore.itemDivisions?.find((d) => d.id === item.item_division_id)
      const parentDivision = databaseStore.itemDivisions?.find(
        (d) => d.id === division?.parent_division_id,
      )
      return {
        item_parent_division_id: parentDivision?.id,
        unit_cost: numberToCurrency(unitCost),
        ...item,
      }
    }) ?? []
  )
})
type Row = (typeof rows.value)[number]

const columns = computed<TableColumn<Row>[]>(() => {
  return [
    {
      name: 'Division',
      columnDataKey: 'item_parent_division_id',
      textDisplayFunction: (value) => {
        const division = databaseStore.itemDivisions?.find((i) => i.id === value)
        return `${division?.code} ${division?.name}`
      },
      defaultWidth: 150,
      deafultHidden: true,
    },
    {
      name: 'Subdivision',
      columnDataKey: 'item_division_id',
      textDisplayFunction: (value) => {
        const division = databaseStore.itemDivisions?.find((i) => i.id === value)
        return `${division?.code} ${division?.name}`
      },
      defaultWidth: 150,
    },
    {
      name: 'Item Code',
      columnDataKey: 'code',
      defaultWidth: 75,
    },
    {
      name: 'Accounting Code',
      columnDataKey: 'accounting_code',
      defaultWidth: 75,
      deafultHidden: true,
    },
    {
      name: 'Name',
      columnDataKey: 'name',
      defaultWidth: 300,
    },
    {
      name: 'UOM',
      columnDataKey: 'uom_id',
      textDisplayFunction: (value) =>
        databaseStore.unitsOfMeasure?.find((u) => u.id === value)?.code ?? '',
      defaultWidth: 75,
    },
    {
      name: 'Unit Cost',
      columnDataKey: 'unit_cost',
      defaultWidth: 75,
    },
  ]
})

const groups = [
  {
    name: 'Division',
    fn: (row: Row) => {
      const subdivision = databaseStore.itemDivisions?.find((d) => d.id === row.item_division_id)
      const division = databaseStore.itemDivisions?.find(
        (d) => d.id === subdivision?.parent_division_id,
      )
      return `${division?.code} ${division?.name}`
    },
  },
]

const selectedRows = ref<Set<Row[keyof Row]>>(new Set())

async function addAssemblyItem(item: Tables<'item'>) {
  const { data, error } = await supabase
    .from('assembly_item')
    .insert({
      id: uuidv7(),
      assembly_id: props.assemblyId,
      item_id: item.id,
    })
    .select()
    .single()
  if (error) {
    throw error
  }
  databaseStore.assemblyItems?.push(data)
  toast(`Added ${item.name}`, { type: 'success' })
}
</script>

<template>
  <div class="flex h-full w-full overflow-auto">
    <BaseTable
      :rows="rows"
      row-key="id"
      :columns="columns"
      :groups="groups"
      table-name="databaseAssemblyEditSelectItems"
      show-row-actions
    >
      <template #rowActions="props">
        <button
          class="w-max cursor-pointer rounded p-1 hover:bg-gray-200"
          @click="addAssemblyItem(props.row)"
        >
          <svg
            class="size-4 text-gray-800"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 12h14m-7 7V5"
            />
          </svg>
        </button>
      </template>
    </BaseTable>
  </div>
</template>
